/**
 * Confirmation dialog for a bulk action on the Call Off Management list
 * (send, cancel or confirm the ticked call-offs).
 *
 * Split out of CallOffManagementTab (CR-7717) to keep that component under the
 * 500-line limit. Presentational: the pending action and its handler arrive
 * as props.
 *
 * @link useCallOffListActions for the state behind it.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import type { CallOffBulkAction } from './useCallOffListActions';

interface CallOffBulkActionDialogProps {
  bulkAction: CallOffBulkAction | null;
  selectedCount: number;
  actionLoading: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

function getBulkActionTitle(bulkAction: CallOffBulkAction | null): string {
  switch (bulkAction) {
    case 'send': return 'Send Call-Offs';
    case 'cancel': return 'Cancel Call-Offs';
    case 'confirm': return 'Confirm Call-Offs';
    default: return '';
  }
}

function getBulkActionDescription(bulkAction: CallOffBulkAction | null, count: number): string {
  switch (bulkAction) {
    case 'send': return `Are you sure you want to send ${count} call-off(s) to the supplier?`;
    case 'cancel': return `Are you sure you want to cancel ${count} call-off(s)? This cannot be undone.`;
    case 'confirm': return `Are you sure you want to confirm ${count} call-off(s)? This will lock them from further editing.`;
    default: return '';
  }
}

function getBulkActionConfirmLabel(bulkAction: CallOffBulkAction | null): string {
  switch (bulkAction) {
    case 'send': return 'Yes, send';
    case 'cancel': return 'Yes, cancel';
    case 'confirm': return 'Yes, confirm';
    default: return 'Confirm';
  }
}

export default function CallOffBulkActionDialog({
  bulkAction, selectedCount, actionLoading, onConfirm, onDismiss,
}: CallOffBulkActionDialogProps) {
  return (
    <AlertDialog open={bulkAction !== null} onOpenChange={(open) => { if (!open) onDismiss(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{getBulkActionTitle(bulkAction)}</AlertDialogTitle>
          <AlertDialogDescription>{getBulkActionDescription(bulkAction, selectedCount)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={actionLoading}>No, go back</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={actionLoading}
            className={bulkAction === 'cancel' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {getBulkActionConfirmLabel(bulkAction)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
